import { ActionIcon, Badge, Button, Flex, Group, Menu, Text, TextInput, Tooltip } from '@mantine/core';
import { IconChevronDown, IconInfoCircle, IconLock } from '@tabler/icons-react';
import { useRef, useState } from 'react';
import usePageSizeDropdown from '../../helpers/usePageSizeDropdown';
import ReactPaginate from '../react-paginate';

// The one-line "what is this screen" orientation, tucked behind an info icon —
// an always-visible explainer on every screen reads as noise once learned.
// The icon sits in an ActionIcon (a real focusable button) because this tooltip is
// now the only place a screen explains itself: on a bare SVG it would be mouse-only.
export const ScreenHint = ({ text }) => (
  <Tooltip label={text} multiline maw={380} withArrow>
    <ActionIcon
      variant="subtle"
      color="gray"
      size="sm"
      aria-label="About this screen"
      style={{ cursor: 'help', flexShrink: 0 }}
    >
      <IconInfoCircle size={16} stroke={1.5} />
    </ActionIcon>
  </Tooltip>
);

// Shared "Showing N of M · pager" footer used by every list screen — same controls
// as the rest of the admin (Pager.jsx: Page Size menu + squared page buttons).
// onPerPageChange receives the number; the caller decides whether to reset the page.
export const PaginationBar = ({ shown, total, noun, page, onPageChange, perPage, onPerPageChange, mt = 'sm' }) => {
  const pageCount = Math.max(1, Math.ceil(total / perPage));
  const pageSizeDropdown = usePageSizeDropdown(perPage, onPerPageChange);

  return (
    <Group justify="space-between" mt={mt} mb="xl" wrap="wrap">
      <Text fz={13} c="dimmed">
        Showing {shown} of {total} {noun}
      </Text>
      <Flex justify="flex-end" align="flex-start" gap="md" wrap="wrap">
        {/* The size menu stays even at one page: picking 100/page collapses the list to a
            single page, and hiding the control along with the pager would strand the
            choice there with no way back to 10/page. */}
        <Menu position="bottom-end">
          <Menu.Target>
            <Button size="sm" rightSection={<IconChevronDown size={14} />}>
              Page Size: {perPage}
            </Button>
          </Menu.Target>
          <Menu.Dropdown>{pageSizeDropdown}</Menu.Dropdown>
        </Menu>
        {pageCount > 1 && (
          <ReactPaginate
            previousLabel="&nbsp;&nbsp;<&nbsp;&nbsp;"
            nextLabel="&nbsp;&nbsp;>&nbsp;&nbsp;"
            breakLabel="..."
            pageCount={pageCount}
            marginPagesDisplayed={2}
            pageRangeDisplayed={5}
            // This ReactPaginate is a vendored 1-based fork, not upstream's 0-based one:
            // page items run 1…pageCount and `forcePage` is used as `selected` directly
            // (see react-paginate/PaginationBoxView.jsx and CompaniesPage.jsx). Passing
            // `selected + 1` / `page - 1` here would fetch the neighbouring page.
            onPageChange={({ selected }) => onPageChange(selected)}
            forcePage={page}
          />
        )}
      </Flex>
    </Group>
  );
};

export const STATUS_COLORS = {
  draft: 'warning',
  validated: 'info',
  published: 'success',
  archived: 'gray',
};

export const STATUS_TOOLTIPS = {
  draft: 'Draft — never served to the AI assistant. Publish to make it live.',
  validated: 'Validated — reviewed but not yet live. Publish to serve it.',
  published: 'Published — live for the AI assistant right now.',
  archived: 'Archived — retired from serving, kept for reference.',
};

export const STAGE_COLORS = {
  pending: 'info',
  parsing: 'info',
  enriching: 'info',
  embedding: 'info',
  indexing: 'info',
  ready: 'success',
  failed: 'danger',
};

export const PIPELINE_STAGES = ['parsing', 'enriching', 'embedding', 'indexing'];

export const SOURCE_TYPE_LABELS = { url: 'URL', html: 'URL', pdf: 'PDF', manual: 'Manual text' };

export const StatusBadge = ({ status, size = 'sm' }) => (
  <Tooltip label={STATUS_TOOLTIPS[status] || status} multiline maw={260} withArrow>
    <Badge variant="light" color={STATUS_COLORS[status] || 'gray'} size={size}>
      {status}
    </Badge>
  </Tooltip>
);

// Unit badges mirror the backend serving rule (helpers/knowledge/serving-state.js):
// under a non-published document every unit is gated by it — a unit's own
// "published" must not read as live.
export const UnitStatusBadge = ({ status, documentStatus, size = 'sm' }) => {
  if (status !== 'published' || !documentStatus || documentStatus === 'published') {
    return <StatusBadge status={status} size={size} />;
  }
  return (
    <Tooltip
      label={`Approved, but the document is ${documentStatus} — nothing serves until the document is published.`}
      multiline
      maw={260}
      withArrow
    >
      <Badge variant="light" color="warning" size={size}>
        published (gated)
      </Badge>
    </Tooltip>
  );
};

export const StageBadge = ({ stage, error, size = 'sm' }) => (
  <Tooltip
    // Failed badge shows the merchant-friendly summary, never the raw error — the
    // raw text lives in the Details panel, superadmin-only.
    label={stage === 'failed' ? interpretIngestError(error).title : `Processing stage: ${stage}`}
    multiline
    maw={260}
    withArrow
  >
    <Badge variant="light" color={STAGE_COLORS[stage] || 'gray'} size={size}>
      {stage}
    </Badge>
  </Tooltip>
);

export const NoVectorBadge = ({ size = 'sm' }) => (
  <Tooltip
    label="No vector yet — served by keyword search only until the background re-embed lands."
    multiline
    maw={260}
    withArrow
  >
    <Badge variant="dot" color="gray" size={size}>
      no vector yet
    </Badge>
  </Tooltip>
);

export const formatMs = (ms) => {
  if (ms == null) return null;
  if (ms < 1000) return `${ms} ms`;
  return `${(ms / 1000).toFixed(1)} s`;
};

export const formatDate = (value) => {
  if (!value) return '—';
  return new Date(value).toLocaleString(undefined, {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
};

export const stripProtocol = (url) => (url || '').replace(/^https?:\/\//, '');

// Turns a raw ingest-job error into merchant-facing guidance: what happened and
// what to do. The raw text is kept separately (superadmin only), never the headline.
// Ordered most-specific first; the fallback covers the unknown.
const INGEST_ERROR_HINTS = [
  {
    match: /too long for ai structuring/i,
    title: 'The page is too large for AI processing',
    hint: 'It was still imported. To reorganize it with AI, split the content into smaller documents first.',
  },
  {
    match: /(ollama|embed|econnrefused|etimedout|embedder|reranker)/i,
    title: 'The AI service was briefly unavailable',
    hint: 'This is temporary — press Retry in a moment. If it keeps happening, contact support.',
  },
  {
    match: /(404|no html|fetch.*failed|extraction failed|could not|enotfound|dns)/i,
    title: "Couldn't open the page",
    hint: 'Check that the URL is public and opens in a browser, then Retry. Pages behind a login can’t be read.',
  },
  // Must stay above the broad "too little text" entry below: an AI-structuring failure
  // reads like "structuring output suspiciously short — produced no usable units", which
  // the broad pattern also matches. The right advice there is "retry without AI", not
  // "paste it manually", so the specific pattern has to win.
  {
    match: /suspiciously short/i,
    title: 'AI processing couldn’t clean this page',
    hint: 'Retry with AI processing turned off to import the page as-is.',
  },
  {
    match: /(too short|junk|no units|produced no)/i,
    title: 'The page had too little readable text',
    hint: 'It may be mostly images or need a login. Paste the text as a manual document instead.',
  },
];

export const interpretIngestError = (raw) => {
  const found = INGEST_ERROR_HINTS.find((entry) => entry.match.test(raw || ''));
  return (
    found || {
      title: 'Processing didn’t finish',
      hint: 'Press Retry. If it keeps failing, contact support.',
    }
  );
};

export const TitleLockIcon = () => (
  <Tooltip label="Manual title — sync won’t change it" withArrow>
    <IconLock size={14} stroke={1.5} color="var(--mantine-color-gray-6)" aria-label="Manual title" />
  </Tooltip>
);

// Enter saves (empty rejected), Esc or blur cancels; stays open while `onSave`'s promise is pending.
export const TitleEditor = ({ value, onSave, onCancel, size = 'sm' }) => {
  const [draft, setDraft] = useState(value || '');
  const [error, setError] = useState(null);
  const saving = useRef(false);

  const save = () => {
    const title = draft.trim();
    if (!title) {
      setError('The title can’t be empty');
      return;
    }
    if (title === value) {
      onCancel();
      return;
    }
    saving.current = true;
    Promise.resolve(onSave(title)).finally(() => {
      saving.current = false;
    });
  };

  return (
    <TextInput
      size={size}
      autoFocus
      aria-label="Document title"
      value={draft}
      error={error}
      style={{ flex: '1 1 240px', minWidth: 200 }}
      onChange={(e) => {
        setDraft(e.currentTarget.value);
        setError(null);
      }}
      onBlur={() => !saving.current && onCancel()}
      onKeyDown={(e) => {
        if (e.key === 'Enter') save();
        if (e.key === 'Escape') onCancel();
      }}
    />
  );
};
